/**
 * Diagnose Parent Link Selection
 * Shows all parentChildLinks for both parent accounts to understand routing
 *
 * USAGE: await window.diagnoseParentLinks()
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase.js';

async function diagnoseParentLinks(schoolIdOverride = null) {
  console.log('🔍 Diagnosing Parent Link Selection...\n');

  try {
    // Get schoolId from context or override
    let schoolId = schoolIdOverride;
    if (!schoolId) {
      // Debug: Check all localStorage keys
      console.log('🔍 Checking localStorage for school context...');
      const keys = Object.keys(localStorage).filter(k => k.includes('swan') || k.includes('school'));
      console.log('Available keys:', keys);

      const contextData = localStorage.getItem('swan_school_context');
      if (!contextData) {
        console.error('❌ No swan_school_context found in localStorage');
        console.log('\nAvailable localStorage data:');
        keys.forEach(k => {
          const val = localStorage.getItem(k);
          console.log(`  ${k}: ${val?.substring(0, 100)}...`);
        });
        console.log('\n💡 Pass schoolId manually: await window.diagnoseParentLinks("your-school-id")');
        return;
      }

      const parsed = JSON.parse(contextData);
      console.log('Context data:', parsed);
      schoolId = parsed.schoolId;
    }

    if (!schoolId) {
      console.error('❌ No schoolId found');
      return;
    }

    console.log(`📚 School ID: ${schoolId}\n`);

    const parent1 = 'POBWRsbLDxZ7atrWitJT7MmZYRY2'; // With Telegram
    const parent2 = 'ptLrdWfIO9PMMQvtIP80OVF0Ksw2'; // Without Telegram

    // Query ALL links for both parents
    const linksSnapshot = await getDocs(
      query(
        collection(db, 'parentChildLinks'),
        where('parentId', 'in', [parent1, parent2])
      )
    );

    console.log(`📊 Found ${linksSnapshot.size} total parent-child links\n`);

    // Group by parent
    const byParent = { [parent1]: [], [parent2]: [] };
    linksSnapshot.docs.forEach(doc => {
      const data = { id: doc.id, ...doc.data() };
      byParent[data.parentId].push(data);
    });

    // Display parent 1 (WITH Telegram)
    console.log(`👤 Parent 1 (WITH Telegram): ${parent1}`);
    console.log(`   Links: ${byParent[parent1].length}`);
    byParent[parent1].forEach(link => {
      console.log(`   - Student: ${link.studentId}`);
      console.log(`     Status: ${link.status}`);
      console.log(`     Telegram Verified: ${link.telegram?.verified || false}`);
      console.log(`     Chat ID: ${link.telegram?.chatId || 'NONE'}\n`);
    });

    // Display parent 2 (WITHOUT Telegram)
    console.log(`👤 Parent 2 (WITHOUT Telegram): ${parent2}`);
    console.log(`   Links: ${byParent[parent2].length}`);
    byParent[parent2].forEach(link => {
      console.log(`   - Student: ${link.studentId}`);
      console.log(`     Status: ${link.status}`);
      console.log(`     Telegram Verified: ${link.telegram?.verified || false}`);
      console.log(`     Chat ID: ${link.telegram?.chatId || 'NONE'}\n`);
    });

    // Check for duplicate students
    const allStudents = linksSnapshot.docs.map(d => d.data().studentId);
    const duplicates = allStudents.filter((id, idx) => allStudents.indexOf(id) !== idx);
    if (duplicates.length > 0) {
      console.log(`⚠️  Duplicate student links found: ${[...new Set(duplicates)].join(', ')}`);
    }

    return { parent1Links: byParent[parent1], parent2Links: byParent[parent2] };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

export { diagnoseParentLinks };
